/**
 * Debounced search analytics for Typesense-powered search (TEC-443).
 * Fires `search` and `search_no_results` events with custom dimensions to
 * GA4 and Ahrefs. This is the ONLY per-search event emission (trackSearchQuery
 * just stores the queryID) — do not add a second one.
 */

import type { SrpSort } from '../search-router/srp-sort';
import { sendGA4RawEvent } from './providers/ga4';
import { sendAhrefsWebAnalyticsRawEvent } from './providers/ahrefs-web-analytics';
import { isClientAnalyticsAllowed } from '../cookie-consent/client-analytics-allowed';

export interface SearchAnalyticsGa4Params {
  search_term: string;
  search_locale: string;
  search_result_count: number;
  search_has_results: boolean;
  search_filters: string;
  search_sort: string;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingParams: SearchAnalyticsGa4Params | null = null;

const DEBOUNCE_MS = 1000;

function fireSearchAnalyticsEvents(params: SearchAnalyticsGa4Params): void {
  if (!isClientAnalyticsAllowed() || typeof window === 'undefined') return;

  const searchPayload = {
    search_term: params.search_term,
    search_locale: params.search_locale,
    search_result_count: params.search_result_count,
    search_has_results: params.search_has_results,
    search_filters: params.search_filters,
    search_sort: params.search_sort,
  };
  sendGA4RawEvent('search', searchPayload);
  sendAhrefsWebAnalyticsRawEvent('search', searchPayload);

  if (!params.search_has_results) {
    const noResultsPayload = {
      search_term: params.search_term,
      search_locale: params.search_locale,
      search_filters: params.search_filters,
    };
    sendGA4RawEvent('search_no_results', noResultsPayload);
    sendAhrefsWebAnalyticsRawEvent('search_no_results', noResultsPayload);
  }
}

/**
 * Schedule GA4 search analytics with 1s debounce to avoid keystroke noise.
 */
export function trackSearchAnalyticsGa4Debounced(params: SearchAnalyticsGa4Params): void {
  pendingParams = params;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (pendingParams) {
      fireSearchAnalyticsEvents(pendingParams);
      pendingParams = null;
    }
    debounceTimer = null;
  }, DEBOUNCE_MS);
}

/** Immediate flush — useful in tests. */
export function flushSearchAnalyticsGa4ForTests(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (pendingParams) {
    fireSearchAnalyticsEvents(pendingParams);
    pendingParams = null;
  }
}

export function buildSearchFiltersJson(filters: unknown): string {
  try {
    return JSON.stringify(filters ?? {});
  } catch {
    return '{}';
  }
}

export function resolveSearchSortLabel(sort: SrpSort): string {
  if (sort === 'date') return 'postedDate:desc';
  if (sort === 'salary') return 'salaryRangeMin:desc';
  return 'score';
}
